import {
  EntitiesGetResponse,
  Entity,
  EntityCreateResponse,
  ResponseEnvelop,
  ServerResponse
} from './responses';

export enum Resource {
  User = 'user',
  Users = 'users',
  Location = 'location',
  Locations = 'locations',
  Department = 'department',
  Departments = 'departments',
  CostCenter = 'cost-center',
  CostCenters = 'cost-centers',
  ExpenseCode = 'expense-code',
  ExpenseCodes = 'expense-codes',
  Allowance = 'allowance',
  Allowances = 'allowances',
  Reports = 'reports',
  Subscription = 'subscription',
  GiftCard = 'giftcard',
  Upgrade = 'upgrade',
  Company = 'company'
}

export interface Department extends Entity {
  total_users: number;
  name: string;
}

export interface DepartmentsResponse extends ServerResponse {
  data: Department[];
  total: number;
}

export interface DepartmentResponse extends ServerResponse {
  data: Department;
}

export interface DepartmentPayload {
  name: string;
}

export type ReportsResponse = EntitiesGetResponse<ReportOrder[]>;

export interface DownloadReportResponse extends ServerResponse {
  data: ArrayBuffer;
}

export type ReportResponse = EntityCreateResponse;

export interface ReportOrder extends Entity {
  code: string;
  collection_type: CollectionType;
  created_at: string;
  delivery_time: string;
  email: string;
  id: number;
  payment_type: OrderType;
  state: OrderStatus;
  total_value: string;
  user_name: string;
}

export enum ReportFormat {
  csv = 'csv',
  xlsx = 'xlsx'
}

export enum OrderStatus {
  arriving = 'arriving',
  cancelled = 'cancelled',
  completed = 'completed',
  customer_picked_up = 'customer_picked_up',
  delivered = 'delivered',
  payment_pending = 'payment_pending',
  picked_up = 'picked_up',
  placed = 'placed',
  preparing = 'preparing'
}

export enum OrderType {
  individual = 'Individual',
  group = 'Group'
}

export enum CollectionType {
  delivery = 'Delivered',
  pickup = 'Pick-up',
  dinein = 'Dine-in'
}

export interface Allowance extends Entity {
  name?: string;
  allow_flexible_locations: boolean;
  allowance_date_from: string;
  allowance_date_to?: string;
  applicable_locations: number[];
  applicable_time_from: string;
  applicable_time_to: string;
  applicable_type: 'user' | 'group';
  applicable_users: string[];
  applicable_users_details?: UserDetails[];
  applicable_weekdays: (
    | 'Mon'
    | 'Tue'
    | 'Wed'
    | 'Thu'
    | 'Fri'
    | 'Sat'
    | 'Sun'
  )[];
  // billing_type: string;
  budget: number;
  budget_type: 'daily' | 'weekly' | 'monthly' | 'custom';
  company_departments?: CompanyDepartments[];
  company_locations?: CompanyLocation[];
  expense_code_required_in_checkout: boolean;
  is_pickup: boolean;
  is_delivery: boolean;
  is_restaurant: boolean;
  is_shop: boolean;
  is_voucher: boolean;
  is_catering: boolean;
  meals_included: boolean;
  ordering_rule_company_department: number[];
  state: AllowanceStates;
  welcome_email_sent_timestamp?: string;
}

export enum AllowanceStates {
  Active = 'active',
  Inactive = 'inactive',
  Expired = 'expired',
  Deleted = 'deleted'
}

export type CompanyDepartments = {
  id: number;
  name: string;
};

export type CompanyLocation = {
  id: number;
  name: string;
};

export type UserDetails = {
  email: string;
  first_name: string;
  id: number;
  last_name: string;
};

export type AllowancesResponse = EntitiesGetResponse<Allowance[]>;

export type AllowanceResponse = ResponseEnvelop<Allowance>;

export interface AllowanceNotifyResonse {
  num_users_sent: number;
}

export enum AllowanceBudgetType {
  Daily = 'daily',
  Weekly = 'weekly',
  Monthly = 'monthly',
  Custom = 'custom'
}

export interface AllowancePayload {
  name: string;
  allow_flexible_locations: boolean;
  allowance_date_from: string;
  allowance_date_to?: string;
  applicable_company_locations: number[];
  applicable_time_from: string;
  applicable_time_to: string;
  applicable_type: 'user' | 'group';
  applicable_users: string[];
  applicable_weekdays: (
    | 'Mon'
    | 'Tue'
    | 'Wed'
    | 'Thu'
    | 'Fri'
    | 'Sat'
    | 'Sun'
  )[];
  budget: number;
  budget_type: AllowanceBudgetType;
  expense_code_required_in_checkout: boolean;
  is_catering: boolean;
  is_delivery: boolean;
  is_pickup: boolean;
  is_restaurant: boolean;
  is_shop: boolean;
  is_voucher: boolean;
  meals_included: boolean;
  ordering_rule_company_department: number[];
}

export interface OrdersStatsType {
  total_order: number;
  total_expense: number;
  avg_order_expense: number;
}

export type OrdersStatsResponse = ResponseEnvelop<OrdersStatsType>;

export interface DashboardStats {
  pending_employees: number;
  active_employees: number;
  active_subscription: number;
  gift_cards_count: number;
  allowances_count: number;
  resend_activation_emails_triggered_at?: string;
}

export type DashboardStatsResponse = ResponseEnvelop<DashboardStats>;
